<template>
  <div class="date-time-module-wrappers">
    <VDateTimePicker
      v-if="presentation === 'date-time'"
      :modelValue="vividValue"
      @update:modelValue="onDateTimeChange"
      :min="calculateMin"
      :max="calculateMax"
      :disabled="disabled"
      :label="label"
      class="date-time-picker"
    />
    <VDatePicker
      v-else
      :modelValue="vividValue"
      @update:modelValue="onDateChange"
      :min="calculateMinDate"
      :max="calculateMaxDate"
      :disabled="disabled"
      :label="label"
      class="date-time-picker"
    />
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { useUIStore } from '@/stores/ui';

export default {
  emits: ['update:modelValue'],
  props: {
    id: {
      type: String,
      default: () => new Date().getTime().toString() + parseInt(Math.random() * 100),
    },
    modelValue: {
      type: String
    },
    minValidation: {
      type: Object,
      default: () => ({ enabled: false })
    },
    maxValidation: {
      type: Object,
      default: () => ({ enabled: false })
    },
    format: {
      type: String,
      default: 'MM/DD/YYYY, HH:mm:ss'
    },
    presentation: {
      type: String,
      default: 'date-time'
    },
    timezone: {
      type: String,
      default: moment.tz.guess()
    },
    autoSetUndefined: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    }
  },
  computed: {
    // Convert the modelValue (formatted string) to ISO format for Vivid
    vividValue() {
      if (!this.modelValue) return '';
      if (this.presentation === 'date') {
        // VDatePicker expects YYYY-MM-DD
        return moment(this.modelValue, this.format).format('YYYY-MM-DD');
      }
      // VDateTimePicker expects full ISO or YYYY-MM-DDTHH:mm:ss
      return moment(this.modelValue, this.format).format('YYYY-MM-DDTHH:mm:ss');
    },
    // For VDateTimePicker: full ISO datetime string
    calculateMin() {
      if (this.minValidation.enabled) {
        return moment().tz(this.timezone).add(this.minValidation.value, this.minValidation.type).format('YYYY-MM-DDTHH:mm:ss');
      }
      return '';
    },
    calculateMax() {
      if (this.maxValidation.enabled) {
        return moment().tz(this.timezone).add(this.maxValidation.value, this.maxValidation.type).format('YYYY-MM-DDTHH:mm:ss');
      }
      return '';
    },
    // For VDatePicker: date-only string
    calculateMinDate() {
      if (this.minValidation.enabled) {
        return moment().tz(this.timezone).add(this.minValidation.value, this.minValidation.type).format('YYYY-MM-DD');
      }
      return '';
    },
    calculateMaxDate() {
      if (this.maxValidation.enabled) {
        return moment().tz(this.timezone).add(this.maxValidation.value, this.maxValidation.type).format('YYYY-MM-DD');
      }
      return '';
    }
  },
  mounted() {
    if (!this.modelValue && this.autoSetUndefined) {
      if (this.minValidation.enabled) {
        this.$emit('update:modelValue', moment(this.calculateMin).tz(this.timezone).format(this.format));
      } else {
        this.$emit('update:modelValue', moment().tz(this.timezone).format(this.format));
      }
    }
  },
  methods: {
    onDateTimeChange(newValue) {
      if (!newValue) return;
      try {
        const formattedDate = moment(newValue).format(this.format);
        this.$emit('update:modelValue', formattedDate);
      } catch (error) {
        useUIStore().updateToast({ open: true, message: "Something went wrong while selecting date and time.", type: "danger" });
      }
    },
    onDateChange(newValue) {
      if (!newValue) return;
      try {
        const formattedDate = moment(newValue, 'YYYY-MM-DD').format(this.format);
        this.$emit('update:modelValue', formattedDate);
      } catch (error) {
        useUIStore().updateToast({ open: true, message: "Something went wrong while selecting date.", type: "danger" });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.date-time-module-wrappers {
  width: 100%;
}
.date-time-picker {
  width: 100%;
}
</style>
